// src/components/homepage/Signup.js
//
// Public "Get Started" signup / intake form. Collects student + parent
// information and submits to the `submitPublicSignup` Cloud Function, which
// creates a student-role account (no password yet) and a /students record so
// admins see the new student in the Student Directory immediately.
//
// Design matches the rest of the public site: blue/white, Inter font, the same
// gradient pill CTA used in the header and Login.

import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebaseConfig";
import PublicHeader from "./public/PublicHeader";
import PublicFooter from "./public/PublicFooter";

const FONT = "'Inter', system-ui, -apple-system, BlinkMacSystemFont, sans-serif";

// Grade options. Numeric grades 9–12 unlock the optional academic fields.
const GRADE_OPTIONS = [
  "Pre-K",
  "Kindergarten",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "11",
  "12",
  "College",
  "Other",
];

const PROGRAM_OPTIONS = [
  "Enrichment",
  "SAT",
  "ACT",
  "School Grades / GPA Help",
  "Advanced Placement (AP) Help",
  "College Essay Help",
  "Strengthen Foundation",
];

const FOUND_OUT_OPTIONS = [
  "Search Engine Result (Google)",
  "Google Ad",
  "Facebook Ad",
  "Other Social Media Ad",
  "Walk-In / Saw Store",
  "Billboard Ad",
  "Korean Radio Ad",
  "Korean Newspaper Ad",
  "Saw Lawn Sign",
  "Saw True Education at Event",
  "Referral",
];

const HOW_SOON_OPTIONS = [
  "As soon as possible",
  "Within 2 weeks",
  "Within a month",
  "Within a few months",
  "Just exploring options",
];

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#f7f9fc",
    fontFamily: FONT,
    color: "#0f172a",
  },
  main: {
    flex: 1,
    width: "100%",
    maxWidth: "760px",
    margin: "0 auto",
    padding: "36px 16px 60px",
    boxSizing: "border-box",
  },
  heroEyebrow: {
    fontSize: "12px",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#1d4ed8",
    marginBottom: "8px",
  },
  heroTitle: {
    fontSize: "28px",
    fontWeight: 800,
    color: "#0b1530",
    margin: "0 0 8px",
    lineHeight: 1.2,
  },
  heroSub: {
    fontSize: "14px",
    color: "#64748b",
    margin: "0 0 24px",
    lineHeight: 1.6,
  },
  card: {
    backgroundColor: "#ffffff",
    border: "1px solid #e6ebf3",
    borderRadius: "20px",
    padding: "24px",
    boxShadow: "0 24px 50px rgba(15,23,42,0.08)",
  },
  section: {
    marginBottom: "26px",
  },
  sectionTitle: {
    fontSize: "13px",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#0b1530",
    margin: "0 0 4px",
  },
  sectionHint: {
    fontSize: "12px",
    color: "#94a3b8",
    margin: "0 0 14px",
  },
  grid2: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "14px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
  },
  label: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#334155",
    marginBottom: "6px",
  },
  req: { color: "#dc2626", marginLeft: "2px" },
  input: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "10px",
    border: "1px solid #d6dee9",
    fontSize: "14px",
    outline: "none",
    backgroundColor: "#fbfdff",
    boxSizing: "border-box",
    fontFamily: FONT,
    color: "#0f172a",
  },
  textarea: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "10px",
    border: "1px solid #d6dee9",
    fontSize: "14px",
    outline: "none",
    backgroundColor: "#fbfdff",
    boxSizing: "border-box",
    fontFamily: FONT,
    color: "#0f172a",
    resize: "vertical",
    minHeight: "72px",
  },
  checkGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "8px",
  },
  checkRow: {
    display: "flex",
    alignItems: "center",
    gap: "9px",
    padding: "9px 12px",
    border: "1px solid #e2e8f0",
    borderRadius: "10px",
    fontSize: "14px",
    cursor: "pointer",
    backgroundColor: "#ffffff",
    color: "#334155",
    userSelect: "none",
  },
  checkRowActive: {
    borderColor: "#1d4ed8",
    backgroundColor: "#eef4ff",
    color: "#1d4ed8",
    fontWeight: 600,
  },
  hsBanner: {
    fontSize: "12px",
    color: "#475569",
    backgroundColor: "#eef4ff",
    border: "1px solid #d9e4ff",
    borderRadius: "10px",
    padding: "8px 12px",
    marginBottom: "14px",
  },
  submitBtn: {
    width: "100%",
    marginTop: "8px",
    padding: "13px 16px",
    borderRadius: "999px",
    border: "none",
    background: "linear-gradient(135deg, #1d4ed8, #0b1530)",
    color: "#ffffff",
    fontSize: "15px",
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0 14px 30px rgba(29,78,216,0.30)",
    fontFamily: FONT,
  },
  submitBtnDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  errorBox: {
    marginBottom: "16px",
    padding: "10px 12px",
    borderRadius: "10px",
    backgroundColor: "#fef2f2",
    border: "1px solid #fecaca",
    fontSize: "13px",
    color: "#b91c1c",
  },
  footerNote: {
    fontSize: "12px",
    color: "#94a3b8",
    textAlign: "center",
    marginTop: "14px",
    lineHeight: 1.6,
  },
  loginLink: {
    color: "#1d4ed8",
    fontWeight: 600,
    cursor: "pointer",
    textDecoration: "none",
    background: "none",
    border: "none",
    padding: 0,
    font: "inherit",
  },
  // Success screen
  successWrap: { textAlign: "center", padding: "8px 4px" },
  successBadge: {
    width: "56px",
    height: "56px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #1d4ed8, #0b1530)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "28px",
    margin: "0 auto 16px",
  },
};

function TextField({ label, required, type = "text", value, onChange, placeholder, autoComplete }) {
  return (
    <div style={styles.field}>
      <label style={styles.label}>
        {label}
        {required && <span style={styles.req}>*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        style={styles.input}
      />
    </div>
  );
}

export default function Signup() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    studentFullName: "",
    studentGrade: "",
    studentEmail: "",
    studentPhone: "",
    parentName: "",
    parentPhone: "",
    parentEmail: "",
    foundOut: "",
    foundOutOther: "",
    school: "",
    homeAddress: "",
    studentDob: "",
    howSoon: "",
    careersMajors: "",
    programsOther: "",
    gpa: "",
    satScore: "",
    actScore: "",
  });
  const [programs, setPrograms] = useState([]);
  const [programsOtherChecked, setProgramsOtherChecked] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key) => (val) => setForm((f) => ({ ...f, [key]: val }));

  const isHighSchool = useMemo(() => {
    const n = Number(form.studentGrade);
    return Number.isFinite(n) && n >= 9 && n <= 12;
  }, [form.studentGrade]);

  const toggleProgram = (program) => {
    setPrograms((prev) =>
      prev.includes(program) ? prev.filter((p) => p !== program) : [...prev, program]
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    // Client-side required validation (mirrors the server checks).
    const required = [
      ["studentFullName", "Student full name"],
      ["studentGrade", "Student grade"],
      ["studentEmail", "Student email"],
      ["studentPhone", "Student phone"],
      ["parentName", "Parent/guardian name"],
      ["parentPhone", "Parent phone"],
      ["parentEmail", "Parent email"],
      ["foundOut", "How you found out about us"],
    ];
    const firstMissing = required.find(([k]) => !String(form[k] || "").trim());
    if (firstMissing) {
      setError(`Please fill out the required field: ${firstMissing[1]}.`);
      return;
    }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(form.studentEmail.trim())) {
      setError("Please enter a valid student email address.");
      return;
    }
    if (!emailRe.test(form.parentEmail.trim())) {
      setError("Please enter a valid parent email address.");
      return;
    }

    // Resolve "Others…" free-text answers.
    const foundOutResolved =
      form.foundOut === "Others…" ? form.foundOutOther.trim() || "Other" : form.foundOut;

    const programsResolved = [...programs];
    if (programsOtherChecked && form.programsOther.trim()) {
      programsResolved.push(form.programsOther.trim());
    }

    const payload = {
      studentFullName: form.studentFullName,
      studentGrade: form.studentGrade,
      studentEmail: form.studentEmail,
      studentPhone: form.studentPhone,
      parentName: form.parentName,
      parentPhone: form.parentPhone,
      parentEmail: form.parentEmail,
      foundOut: foundOutResolved,
      school: form.school,
      homeAddress: form.homeAddress,
      studentDob: form.studentDob,
      howSoon: form.howSoon,
      careersMajors: form.careersMajors,
      programs: programsResolved,
      // Only meaningful for grades 9–12; server also guards this.
      gpa: isHighSchool ? form.gpa : "",
      satScore: isHighSchool ? form.satScore : "",
      actScore: isHighSchool ? form.actScore : "",
    };

    setSubmitting(true);
    try {
      const submit = httpsCallable(functions, "submitPublicSignup");
      const res = await submit(payload);
      const data = res?.data || {};
      if (data.status === "account_exists") {
        setError(
          "An account already exists for this student email. Please use the Login page to sign in or set your password."
        );
        setSubmitting(false);
        return;
      }
      setDone(true);
    } catch (err) {
      setError(
        err?.message ||
          "Something went wrong submitting your signup. Please try again, or contact us if it keeps happening."
      );
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.page}>
      <PublicHeader />
      <main style={styles.main}>
        {done ? (
          <div style={styles.card}>
            <div style={styles.successWrap}>
              <div style={styles.successBadge}>✓</div>
              <h1 style={styles.heroTitle}>Thank you for getting started</h1>
              <p style={styles.heroSub}>
                Thank you for getting started with True Education Cumming. Your information has been
                submitted successfully, and our team will reach out soon.
              </p>
              <button type="button" style={styles.submitBtn} onClick={() => navigate("/")}>
                Back to Home
              </button>
            </div>
          </div>
        ) : (
          <>
            <div style={styles.heroEyebrow}>Get Started</div>
            <h1 style={styles.heroTitle}>Sign up for True Education Cumming</h1>
            <p style={styles.heroSub}>
              Tell us about your student and we&rsquo;ll get you set up. Fields marked
              <span style={styles.req}>*</span> are required.
            </p>

            <form onSubmit={handleSubmit} noValidate>
              <div style={styles.card}>
                {error && <div style={styles.errorBox}>{error}</div>}

                {/* Student */}
                <div style={styles.section}>
                  <h2 style={styles.sectionTitle}>Student Information</h2>
                  <p style={styles.sectionHint}>Details about the student who will be learning with us.</p>
                  <div style={styles.grid2}>
                    <TextField
                      label="Student full name"
                      required
                      value={form.studentFullName}
                      onChange={set("studentFullName")}
                      autoComplete="name"
                    />
                    <div style={styles.field}>
                      <label style={styles.label}>
                        Student grade<span style={styles.req}>*</span>
                      </label>
                      <select
                        value={form.studentGrade}
                        onChange={(e) => set("studentGrade")(e.target.value)}
                        style={styles.input}
                      >
                        <option value="">Select grade…</option>
                        {GRADE_OPTIONS.map((g) => (
                          <option key={g} value={g}>
                            {/^\d+$/.test(g) ? `Grade ${g}` : g}
                          </option>
                        ))}
                      </select>
                    </div>
                    <TextField
                      label="Student email"
                      required
                      type="email"
                      value={form.studentEmail}
                      onChange={set("studentEmail")}
                      placeholder="student@example.com"
                      autoComplete="email"
                    />
                    <TextField
                      label="Student phone"
                      required
                      type="tel"
                      value={form.studentPhone}
                      onChange={set("studentPhone")}
                      autoComplete="tel"
                    />
                    <TextField
                      label="School name"
                      value={form.school}
                      onChange={set("school")}
                    />
                    <TextField
                      label="Student date of birth"
                      type="date"
                      value={form.studentDob}
                      onChange={set("studentDob")}
                    />
                  </div>
                </div>

                {/* High-school-only academics */}
                {isHighSchool && (
                  <div style={styles.section}>
                    <h2 style={styles.sectionTitle}>Academics (Grades 9–12)</h2>
                    <div style={styles.hsBanner}>
                      These academic fields are optional — share whatever you have on hand.
                    </div>
                    <div style={styles.grid2}>
                      <TextField label="GPA" value={form.gpa} onChange={set("gpa")} placeholder="e.g. 3.8" />
                      <TextField
                        label="SAT score"
                        value={form.satScore}
                        onChange={set("satScore")}
                        placeholder="e.g. 1380"
                      />
                      <TextField
                        label="ACT score"
                        value={form.actScore}
                        onChange={set("actScore")}
                        placeholder="e.g. 31"
                      />
                    </div>
                  </div>
                )}

                {/* Parent / Guardian */}
                <div style={styles.section}>
                  <h2 style={styles.sectionTitle}>Parent / Guardian Information</h2>
                  <div style={styles.grid2}>
                    <TextField
                      label="Parent/guardian name"
                      required
                      value={form.parentName}
                      onChange={set("parentName")}
                    />
                    <TextField
                      label="Parent phone"
                      required
                      type="tel"
                      value={form.parentPhone}
                      onChange={set("parentPhone")}
                    />
                    <TextField
                      label="Parent email"
                      required
                      type="email"
                      value={form.parentEmail}
                      onChange={set("parentEmail")}
                    />
                  </div>
                  <div style={{ ...styles.field, marginTop: "14px" }}>
                    <label style={styles.label}>Home address</label>
                    <textarea
                      value={form.homeAddress}
                      onChange={(e) => set("homeAddress")(e.target.value)}
                      style={styles.textarea}
                      placeholder="Street, City, State, ZIP"
                    />
                  </div>
                </div>

                {/* Programs */}
                <div style={styles.section}>
                  <h2 style={styles.sectionTitle}>Which Programs Are You Interested In?</h2>
                  <p style={styles.sectionHint}>Select all that apply (optional).</p>
                  <div style={styles.checkGrid}>
                    {PROGRAM_OPTIONS.map((p) => {
                      const active = programs.includes(p);
                      return (
                        <label
                          key={p}
                          style={{ ...styles.checkRow, ...(active ? styles.checkRowActive : {}) }}
                        >
                          <input
                            type="checkbox"
                            checked={active}
                            onChange={() => toggleProgram(p)}
                          />
                          {p}
                        </label>
                      );
                    })}
                    <label
                      style={{
                        ...styles.checkRow,
                        ...(programsOtherChecked ? styles.checkRowActive : {}),
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={programsOtherChecked}
                        onChange={() => setProgramsOtherChecked((v) => !v)}
                      />
                      Others…
                    </label>
                  </div>
                  {programsOtherChecked && (
                    <div style={{ ...styles.field, marginTop: "12px" }}>
                      <label style={styles.label}>Please describe</label>
                      <input
                        type="text"
                        value={form.programsOther}
                        onChange={(e) => set("programsOther")(e.target.value)}
                        style={styles.input}
                        placeholder="Tell us what you're looking for"
                      />
                    </div>
                  )}
                </div>

                {/* How soon */}
                <div style={styles.section}>
                  <h2 style={styles.sectionTitle}>How Soon Do You Need Help?</h2>
                  <p style={styles.sectionHint}>Optional.</p>
                  <div style={styles.field}>
                    <select
                      value={form.howSoon}
                      onChange={(e) => set("howSoon")(e.target.value)}
                      style={styles.input}
                    >
                      <option value="">Select…</option>
                      {HOW_SOON_OPTIONS.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* How did you find out — REQUIRED */}
                <div style={styles.section}>
                  <h2 style={styles.sectionTitle}>
                    How Did You First Find Out About Us?<span style={styles.req}>*</span>
                  </h2>
                  <div style={styles.field}>
                    <select
                      value={form.foundOut}
                      onChange={(e) => set("foundOut")(e.target.value)}
                      style={styles.input}
                    >
                      <option value="">Select…</option>
                      {FOUND_OUT_OPTIONS.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                      <option value="Others…">Others…</option>
                    </select>
                  </div>
                  {form.foundOut === "Others…" && (
                    <div style={{ ...styles.field, marginTop: "12px" }}>
                      <label style={styles.label}>Please tell us</label>
                      <input
                        type="text"
                        value={form.foundOutOther}
                        onChange={(e) => set("foundOutOther")(e.target.value)}
                        style={styles.input}
                      />
                    </div>
                  )}
                </div>

                {/* Careers / majors */}
                <div style={styles.section}>
                  <h2 style={styles.sectionTitle}>Interested Careers / Majors</h2>
                  <p style={styles.sectionHint}>Optional — helps us tailor guidance.</p>
                  <div style={styles.field}>
                    <textarea
                      value={form.careersMajors}
                      onChange={(e) => set("careersMajors")(e.target.value)}
                      style={styles.textarea}
                      placeholder="e.g. Computer Science, Pre-Med, Business…"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  style={{ ...styles.submitBtn, ...(submitting ? styles.submitBtnDisabled : {}) }}
                >
                  {submitting ? "Submitting…" : "Submit Signup"}
                </button>

                <p style={styles.footerNote}>
                  Already have an account?{" "}
                  <button type="button" style={styles.loginLink} onClick={() => navigate("/login")}>
                    Log in here
                  </button>
                  .
                </p>
              </div>
            </form>
          </>
        )}
      </main>
      <PublicFooter />
    </div>
  );
}
